import type { Object as PortalObject } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import { ExternalLink, FileText, Link2, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useObject, useUpdateObject } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@/shared/components/ui';

type ObjectFileEntry = NonNullable<PortalObject['files']>[number];

const buildFileLabel = (file: Pick<ObjectFileEntry, 'name' | 'link'>) =>
  file.name || file.link || '';

type ObjectFileFormValues = {
  name: string;
  link: string;
  description?: string;
};

export interface ObjectFilesProps {
  objectId?: string;
  object?: PortalObject;
  canManage: boolean;
  readOnly?: boolean;
  variant?: 'compact' | 'full';
  showHeader?: boolean;
}

export function ObjectFiles({
  objectId,
  object: objectProp,
  canManage,
  readOnly = false,
  variant = 'full',
  showHeader = true,
}: ObjectFilesProps) {
  const { t } = useIntl();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fileToEdit, setFileToEdit] = useState<ObjectFileEntry | null>(null);
  const [fileToDelete, setFileToDelete] = useState<ObjectFileEntry | null>(null);

  const { data: objectData, isLoading } = useObject(objectId || '', {
    disabled: !objectId || !!objectProp,
  });

  const object = objectProp || objectData;
  const [localFiles, setLocalFiles] = useState<ObjectFileEntry[]>([]);
  const files = useMemo(() => localFiles, [localFiles]);

  useEffect(() => {
    if (!object) return;
    setLocalFiles((Array.isArray(object.files) ? object.files : []) as ObjectFileEntry[]);
  }, [object]);

  const updateObject = useUpdateObject();

  const normalizeLink = (value: string) => {
    const trimmed = value.trim();
    if (/^www\./i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  const formSchema = useMemo(() => {
    const isValidUrl = (value: string) => {
      try {
        const parsed = new URL(normalizeLink(value));
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
      } catch {
        return false;
      }
    };

    return z.object({
      name: z.string().min(1, t('validation.required')),
      link: z
        .string()
        .trim()
        .min(1, t('validation.required'))
        .refine((value) => isValidUrl(value), t('objects.files.linkInvalid')),
      description: z.string().optional(),
    });
  }, [t]);

  const form = useForm<ObjectFileFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      link: '',
      description: '',
    },
  });

  useEffect(() => {
    if (!dialogOpen) return;
    if (fileToEdit) {
      form.reset({
        name: fileToEdit.name || '',
        link: fileToEdit.link || '',
        description: fileToEdit.description || '',
      });
    } else {
      form.reset({
        name: '',
        link: '',
        description: '',
      });
    }
  }, [dialogOpen, fileToEdit, form]);

  const handleOpenCreate = () => {
    if (readOnly) return;
    setFileToEdit(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (file: ObjectFileEntry) => {
    if (readOnly) return;
    setFileToEdit(file);
    setDialogOpen(true);
  };

  const handleSubmit = async (values: ObjectFileFormValues) => {
    if (!objectId) return;
    const previousFiles = files;
    try {
      const normalizedValues = {
        ...values,
        link: normalizeLink(values.link),
      };
      const nextFiles = fileToEdit
        ? files.map((file) =>
            file.id === fileToEdit.id
              ? {
                  ...file,
                  ...normalizedValues,
                }
              : file
          )
        : [...files, { ...normalizedValues }];

      setLocalFiles(nextFiles);
      const updated = await updateObject.mutateAsync({
        id: objectId,
        data: {
          files: nextFiles,
        },
      });

      if (updated) {
        setLocalFiles((Array.isArray(updated.files) ? updated.files : []) as ObjectFileEntry[]);
      }
      toast.success(
        fileToEdit ? t('objects.files.updateSuccess') : t('objects.files.createSuccess')
      );
      setDialogOpen(false);
    } catch (_error) {
      setLocalFiles(previousFiles);
      toast.error(t('objects.files.saveError'));
    }
  };

  const handleDelete = async () => {
    if (!objectId) return;
    if (!fileToDelete) return;
    const previousFiles = files;
    try {
      const nextFiles = files.filter((file) => file.id !== fileToDelete.id);
      setLocalFiles(nextFiles);
      const updated = await updateObject.mutateAsync({
        id: objectId,
        data: {
          files: nextFiles,
        },
      });
      if (updated) {
        setLocalFiles((Array.isArray(updated.files) ? updated.files : []) as ObjectFileEntry[]);
      }
      toast.success(t('objects.files.deleteSuccess'));
      setFileToDelete(null);
    } catch (_error) {
      setLocalFiles(previousFiles);
      toast.error(t('objects.files.deleteError'));
    }
  };

  const listContent = isLoading ? (
    <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
  ) : files.length === 0 ? (
    <div className="text-sm text-muted-foreground">{t('objects.files.empty')}</div>
  ) : (
    <div
      className={
        variant === 'compact'
          ? 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3'
          : 'grid gap-4 sm:grid-cols-2 xl:grid-cols-3'
      }
    >
      {files.map((file) => (
        <div
          key={file.id}
          className={
            variant === 'compact'
              ? 'border border-border rounded-xl bg-card p-3 flex flex-col gap-3'
              : 'border border-border rounded-xl bg-card p-4 flex flex-col gap-3'
          }
        >
          <div className="flex flex-col gap-1">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <FileText className="mt-0.5 size-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div
                    className={
                      variant === 'compact'
                        ? 'text-sm font-semibold text-foreground line-clamp-1'
                        : 'text-sm font-semibold text-foreground'
                    }
                  >
                    {file.name || t('common.file')}
                  </div>
                  {file.description && (
                    <div
                      className={
                        variant === 'compact'
                          ? 'text-xs text-muted-foreground line-clamp-2'
                          : 'text-sm text-muted-foreground whitespace-pre-wrap'
                      }
                    >
                      {file.description}
                    </div>
                  )}
                </div>
              </div>
              {canManage && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={() => handleOpenEdit(file)}
                    aria-label={t('objects.files.edit')}
                    disabled={readOnly}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={() => setFileToDelete(file)}
                    aria-label={t('objects.files.delete')}
                    disabled={readOnly}
                  >
                    <Trash2 />
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={file.link} target="_blank" rel="noreferrer">
                <ExternalLink />
                {t('objects.files.open')}
              </a>
            </Button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {showHeader && (
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Link2 className="size-4 text-muted-foreground" />
            <h4 className="text-sm font-semibold text-foreground">{t('objects.files.title')}</h4>
          </div>
          <div className="h-px flex-1 bg-border" />
          {canManage && (
            <Button size="sm" type="button" onClick={handleOpenCreate} disabled={readOnly}>
              {t('objects.files.add')}
            </Button>
          )}
        </div>
      )}
      {listContent}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false);
            setFileToEdit(null);
          } else {
            setDialogOpen(true);
          }
        }}
      >
        <DialogContent>
          <Form {...form}>
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void form.handleSubmit(handleSubmit)();
              }}
            >
              <div className="text-base font-semibold text-foreground">
                {fileToEdit ? t('objects.files.editTitle') : t('objects.files.addTitle')}
              </div>

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('objects.files.nameLabel')}</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={readOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="link"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('objects.files.linkLabel')}</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={readOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('objects.files.descriptionLabel')}</FormLabel>
                    <FormControl>
                      <Textarea {...field} disabled={readOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={updateObject.isPending || readOnly}>
                  {fileToEdit ? t('common.save') : t('common.add')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!fileToDelete} onOpenChange={(open) => !open && setFileToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('objects.files.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('objects.files.deleteConfirm', {
                name: fileToDelete ? buildFileLabel(fileToDelete) : '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              disabled={updateObject.isPending || readOnly}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
